// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Game state normalization and merge helpers.
 *
 * Extracted from gameStore to keep state update logic testable and
 * the preserve-keys list data-driven rather than buried in a closure.
 */

import type { GameState, LobbyState } from '../types/game';

/**
 * Keys that must be preserved across partial game state updates.
 *
 * When the server sends a partial update (e.g. a broadcast after a
 * player action), these keys are carried forward from the previous
 * state so the client never loses critical information.
 *
 * Organised by category so additions are self-documenting.
 */
export const PRESERVE_KEYS: readonly string[] = [
  // Core phase / status
  'phase',
  'player_phase', // Per-player phase (sent via player_updates, not broadcast)
  'room_status',

  // Player lists
  'players',
  'current_players',
  'all_players',
  'player_stats',

  // Round / turn state
  'current_round',
  'current_round_state',
  'current_judge',
  'is_judge',
  'active_player_id',
  'expected_action',

  // Identity flags
  'is_host',
  'your_player_id',
  'game_id',

  // Hand / card data (personalised via player_updates)
  'your_hand',
  'my_hand',
  'my_judge_hand',
  'is_my_turn',
  'available_cards',

  // Configuration
  'config',

  // Character selection (Terrible Psychiatrist)
  'characters',
  'selected_characters',
  'my_character_id',
  'all_characters_selected',

  // Redaction
  'active_memo',
  'redaction_budget',
  'content_mode',
  'redact_time_seconds',
  'vote_time_seconds',
  'submitted_count',
  'my_submission_id',
  'my_vote',
  'submission_order',
  'submissions_public',
  'current_round_result',
  'graph_options',
  'graph_default_selection',

  // Version tracking
  'state_version',
] as const;

type StateRecord = Record<string, unknown>;

/**
 * Flatten a nested `{ game_state: { ... } }` envelope into a flat object.
 * Some server responses wrap game data inside a `game_state` key.
 */
export function normalizeGameState(incoming: GameState | LobbyState): GameState | LobbyState {
  if (
    incoming &&
    typeof incoming === 'object' &&
    'game_state' in incoming &&
    incoming.game_state &&
    typeof incoming.game_state === 'object'
  ) {
    const combined: StateRecord = {
      ...(incoming as unknown as StateRecord),
      ...(incoming.game_state as unknown as StateRecord),
    };
    delete combined.game_state;
    return combined as unknown as GameState | LobbyState;
  }
  return incoming;
}

/**
 * Build the initial game state when no previous state exists.
 * Ensures `phase` is populated from `room_status` if missing.
 */
export function buildInitialState(normalized: GameState | LobbyState): GameState | LobbyState {
  const state = { ...(normalized as unknown as StateRecord) };
  if (!('phase' in state) && typeof state.room_status === 'string') {
    state.phase = state.room_status;
  }
  return state as unknown as GameState | LobbyState;
}

/**
 * Merge incoming partial state with the previous state, preserving
 * critical keys that the server may omit in partial updates.
 *
 * Also recomputes derived fields (e.g. `is_judge` from
 * `current_judge_id`) and ensures `phase` fallback.
 */
export function mergeGameState(
  previous: GameState | LobbyState,
  normalized: GameState | LobbyState,
  currentPlayerId: string | null
): GameState | LobbyState {
  const merged: StateRecord = {
    ...(previous as unknown as StateRecord),
    ...(normalized as unknown as StateRecord),
  };
  const prev: StateRecord = previous as unknown as StateRecord;
  const incoming: StateRecord = normalized as unknown as StateRecord;

  // Carry forward keys missing from the incoming update
  for (const key of PRESERVE_KEYS) {
    if (!(key in incoming) && key in prev) {
      merged[key] = prev[key];
    }
  }

  // Update state version if provided, otherwise preserve or default
  if ('state_version' in incoming) {
    merged['state_version'] = incoming['state_version'];
  } else if ('state_version' in prev) {
    merged['state_version'] = prev['state_version'];
  } else {
    merged['state_version'] = 1;
  }

  // Recompute is_judge when current_judge_id changes
  if ('current_judge_id' in incoming) {
    const yourId = merged.your_player_id || prev.your_player_id || currentPlayerId;
    merged.is_judge = incoming.current_judge_id === yourId;
  }

  // Fallback: if phase missing but room_status present, use room_status
  if (!('phase' in merged) && typeof merged.room_status === 'string') {
    merged.phase = merged.room_status;
  }

  return merged as unknown as GameState | LobbyState;
}
